import { Globe, Input } from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { Facility, SocialMedia } from "@/types/facility.definition";

interface SocialMediaTabProps {
  facility: Facility;
  setFacility: React.Dispatch<React.SetStateAction<Facility>>;
}

export const SocialMediaTab: React.FC<SocialMediaTabProps> = ({
  facility,
  setFacility,
}) => {
  const { t } = useTranslation();
  const [urlErrors, setUrlErrors] = React.useState<
    Partial<Record<keyof SocialMedia, string>>
  >({});

  const validateUrl = (key: keyof SocialMedia, value: string) => {
    if (!value) {
      setUrlErrors((prev) => ({ ...prev, [key]: undefined }));
      return;
    }
    const isWhatsapp = key === "whatsapp";
    const isUrl =
      value.startsWith("http://") || value.startsWith("https://");
    if (!isWhatsapp && !isUrl) {
      setUrlErrors((prev) => ({
        ...prev,
        [key]: t("facilitySocial.invalidUrl"),
      }));
    } else if (isWhatsapp && value.startsWith("http") && !isUrl) {
      setUrlErrors((prev) => ({
        ...prev,
        [key]: t("facilitySocial.invalidUrl"),
      }));
    } else {
      setUrlErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const handleChange =
    (key: keyof SocialMedia) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFacility((prev) => ({
        ...prev,
        social: {
          ...prev.social,
          [key]: value || null,
        },
      }));
    };

  const handleBlur =
    (key: keyof SocialMedia) =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      validateUrl(key, e.target.value);
    };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center mb-2 space-x-3">
          <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
            <Globe className="w-4 h-4 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t("facilitySocial.title")}
          </h3>
        </div>
        <p className="text-sm text-neutral-500 ml-11">
          {t("facilitySocial.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t("facilitySocial.facebook")}
          type="url"
          value={facility.social?.facebook ?? ""}
          onChange={handleChange("facebook")}
          onBlur={handleBlur("facebook")}
          placeholder="https://facebook.com/your-page"
          leftIcon={<Globe size={16} />}
          error={urlErrors.facebook}
        />

        <Input
          label={t("facilitySocial.instagram")}
          type="url"
          value={facility.social?.instagram ?? ""}
          onChange={handleChange("instagram")}
          onBlur={handleBlur("instagram")}
          placeholder="https://instagram.com/your-account"
          leftIcon={<Globe size={16} />}
          error={urlErrors.instagram}
        />

        <Input
          label={t("facilitySocial.twitter")}
          type="url"
          value={facility.social?.twitter ?? ""}
          onChange={handleChange("twitter")}
          onBlur={handleBlur("twitter")}
          placeholder="https://x.com/your-account"
          leftIcon={<Globe size={16} />}
          error={urlErrors.twitter}
        />

        <Input
          label={t("facilitySocial.linkedin")}
          type="url"
          value={facility.social?.linkedin ?? ""}
          onChange={handleChange("linkedin")}
          onBlur={handleBlur("linkedin")}
          placeholder="https://linkedin.com/company/your-page"
          leftIcon={<Globe size={16} />}
          error={urlErrors.linkedin}
        />

        <Input
          label={t("facilitySocial.youtube")}
          type="url"
          value={facility.social?.youtube ?? ""}
          onChange={handleChange("youtube")}
          onBlur={handleBlur("youtube")}
          placeholder="https://youtube.com/@your-channel"
          leftIcon={<Globe size={16} />}
          error={urlErrors.youtube}
        />

        <Input
          label={t("facilitySocial.whatsapp")}
          type="text"
          value={facility.social?.whatsapp ?? ""}
          onChange={handleChange("whatsapp")}
          onBlur={handleBlur("whatsapp")}
          placeholder="+221xxxxxxxxx or https://wa.me/221xxxxxxxxx"
          leftIcon={<Globe size={16} />}
          error={urlErrors.whatsapp}
        />
      </div>
    </div>
  );
};
